import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useTaskRecovery } from 'af-root/hooks/useTaskRecovery';
import FieldReportTypePreviewModal from 'af-root/scenes/Company/FieldReports/WorkOrdersList/ReportTypeViewTable/SidePanel/FieldReportTypeModal';

import type FieldReportTypePreviewInModalVM from 'ab-viewModels/fieldReport/fieldReportTypeSidePanel.viewModel';

import type { SegmentCount } from 'af-utils/fieldReport.util';
import { getSegmentCount } from 'af-utils/fieldReport.util';

import ImagePreviewModal from 'af-components/ImagePreviewModal';

import * as FieldReportActions from 'af-actions/fieldReport';

const ALLOWED_RETRIES = 4;

interface OwnProps {
	headerValue: string;
	fieldReportTypeToPreview: FieldReportTypePreviewInModalVM;
	onClose: () => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const PreviousFieldReportPreviewFRTModal: React.FC<Props> = (props) => {

	const { headerValue, fieldReportTypeToPreview, findOrCreateGroup, downloadFieldReportTypePDF, onClose } = props;
	const [showFieldReportTypeModal, setShowFieldReportTypeModal] = React.useState(true);
	const [currentlyPreviewedImage, setCurrentlyPreviewedImage] = React.useState<Nullable<string>>(null);

	const taskRecovery = useTaskRecovery();

	const closeModal = React.useCallback(() => {
		setShowFieldReportTypeModal(false);
		onClose();
	}, [onClose]);

	const onExportAsPDFClick = React.useCallback((fieldReportTypeIds: number[], isForCustomer: boolean, reportName: string) => async () => {
		const groupCode = await findOrCreateGroup(fieldReportTypeIds, isForCustomer);

		if (!groupCode) {
			return;
		}
		await downloadFieldReportTypePDF(groupCode, reportName, taskRecovery, ALLOWED_RETRIES, isForCustomer);
		// notificationSnackbar and taskRecovery not here but in action
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [downloadFieldReportTypePDF, findOrCreateGroup]);

	const { segmentCountMap, instanceCount } = React.useMemo<{ segmentCountMap: SegmentCount; instanceCount: number; }>(() => {
		const _segmentCountMap = getSegmentCount(fieldReportTypeToPreview?.fieldReportBlocks ?? []);
		const _instanceCount = Object.keys(_segmentCountMap).length;
		return { segmentCountMap: _segmentCountMap, instanceCount: _instanceCount };
	}, [fieldReportTypeToPreview?.fieldReportBlocks]);

	const closeImagePreviewModal = React.useCallback(() => {
		setCurrentlyPreviewedImage(null);
		setShowFieldReportTypeModal(true);
	}, []);

	const onImageClick = React.useCallback((imageSrc: string) => {
		setShowFieldReportTypeModal(false);
		setCurrentlyPreviewedImage(imageSrc);
	}, []);

	return (
		<>
			<FieldReportTypePreviewModal
				closeModal={closeModal}
				currentFieldReportType={fieldReportTypeToPreview}
				fieldReportTypeTitle={headerValue}
				instanceCount={instanceCount}
				onExportAsPDFClick={onExportAsPDFClick}
				onImageClick={onImageClick}
				segmentCountMap={segmentCountMap}
				showModal={showFieldReportTypeModal}
			/>
			{!!currentlyPreviewedImage && (
				<ImagePreviewModal
					closeModal={closeImagePreviewModal}
					imageSrc={currentlyPreviewedImage}
					showModal={true}
				/>
			)}
		</>
	);

};

function mapDispatchToProps() {
	return {
		findOrCreateGroup: FieldReportActions.findOrCreateGroup,
		downloadFieldReportTypePDF: FieldReportActions.downloadFieldReportTypePDF,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(connector);

export default enhance(PreviousFieldReportPreviewFRTModal);
